import {
  type CreateWorkflowCompletionInput,
  WorkflowObjectTypes,
} from '@generated/graphql';
import { PageObjectType } from '@pages/setting/PageConfigurationDetail/pageConfigurationTypes';
import { PAGE_ROOT_WORKFLOW_OBJECT_TYPE } from './pageRootWorkflowObjectType';

export type WorkflowLaunchParams = {
  objectType: WorkflowObjectTypes;
  ownerId?: string;
  objectId?: string;
  withContext: boolean;
};

export type WorkflowLaunchInput = Pick<
  CreateWorkflowCompletionInput,
  'objectType' | 'context'
> &
  Partial<Pick<CreateWorkflowCompletionInput, 'objectId'>>;

export type WorkflowLaunchError = 'missingId' | 'unsupported';

export type WorkflowLaunchResult =
  | { input: WorkflowLaunchInput }
  | { error: WorkflowLaunchError };

// ownerId is the objectId of a USER completion; objectId is the client group of
// a HOUSEHOLD one and the page object of an ACCOUNT, GOAL or SUB_ACCOUNT one.
// Only a flag-on USER input carries a `context` (F5a); the backend derives the
// rest. Flag off, a USER input is unvalidated and context-free (today's).
export const buildWorkflowLaunchInput = ({
  objectType,
  ownerId,
  objectId,
  withContext,
}: WorkflowLaunchParams): WorkflowLaunchResult => {
  if (objectType === WorkflowObjectTypes.Household)
    return { input: { objectType, objectId } };

  if (objectType === WorkflowObjectTypes.User) {
    if (!withContext) return { input: { objectType, objectId: ownerId } };
    if (!ownerId) return { error: 'missingId' };
    return {
      input: {
        objectType,
        objectId: ownerId,
        context: { currentUserId: ownerId },
      },
    };
  }

  if (!withContext) return { error: 'unsupported' };

  switch (objectType) {
    case WorkflowObjectTypes.Account:
    case WorkflowObjectTypes.Goal:
    case WorkflowObjectTypes.SubAccount:
      return objectId
        ? { input: { objectType, objectId } }
        : { error: 'missingId' };
    default:
      return { error: 'unsupported' };
  }
};

export type PageWorkflowLaunchParams = Pick<
  WorkflowLaunchParams,
  'ownerId' | 'withContext'
> & {
  pageType?: PageObjectType;
  pageObjectId?: string;
  workflowObjectType?: WorkflowObjectTypes;
};

// A household page roots a HOUSEHOLD workflow on its client group, flag or not.
// Otherwise, flag off, the declared type is ignored and every page launches a
// USER completion, as before. Flag on, a non-USER workflow must be the type the
// page roots; any other combination is unsupported rather than guessed.
export const buildPageWorkflowLaunchInput = ({
  pageType,
  pageObjectId,
  workflowObjectType = WorkflowObjectTypes.User,
  ownerId,
  withContext,
}: PageWorkflowLaunchParams): WorkflowLaunchResult => {
  if (
    pageType === PageObjectType.HOUSEHOLD &&
    workflowObjectType === WorkflowObjectTypes.Household
  )
    return pageObjectId
      ? buildWorkflowLaunchInput({
          objectType: WorkflowObjectTypes.Household,
          objectId: pageObjectId,
          withContext,
        })
      : { error: 'missingId' };

  if (!withContext || workflowObjectType === WorkflowObjectTypes.User)
    return buildWorkflowLaunchInput({
      objectType: WorkflowObjectTypes.User,
      ownerId,
      withContext,
    });

  if (
    !pageType ||
    workflowObjectType !== PAGE_ROOT_WORKFLOW_OBJECT_TYPE[pageType]
  )
    return { error: 'unsupported' };

  return buildWorkflowLaunchInput({
    objectType: workflowObjectType,
    objectId: pageObjectId,
    withContext,
  });
};
